/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useMemo, useState } from 'react';

import { css, jsx } from '@atlaskit/css';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { SearchTriggerInputContext } from './search-trigger-context';

const EXPANDED_WIDTH = '320px';

const rootStyles = css({
	width: '240px',
	willChange: 'width, opacity, visibility, transform',
	transition:
		'width 0.2s ease-out, opacity 0.2s ease-out, visibility 0.2s ease-out, transform 0.2s ease-out',
});

const hoverFocusStyles = css({
	'&:focus-within': {
		width: EXPANDED_WIDTH,
	},
	'&:hover': {
		width: EXPANDED_WIDTH,
	},
});

const expandedStyles = css({
	width: EXPANDED_WIDTH,
});

const boxStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	display: 'flex',
	alignItems: 'stretch',
	borderRadius: token('radius.full'),
	backgroundColor: token('elevation.surface'),
	gap: token('space.075'),
	boxShadow: token('elevation.shadow.raised'),
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	borderColor: token('color.background.accent.gray.subtlest'),
	opacity: 1,
	visibility: 'visible',
	backfaceVisibility: 'hidden',
	transform: 'translate3d(0, 0, 0) perspective(1px)',
	transition:
		'border-color 0.2s ease-out, opacity 0.2s ease-out, visibility 0.2s ease-out, transform 0.2s ease-out',
	borderStyle: 'solid',
	paddingInlineEnd: token('space.100'),
	font: token('font.body.large'),
	color: token('color.text'),
	height: '56px',
});

const subtlePlaceholderStyles = css({
	color: token('color.text.subtlest'),
});

const layoutStyles = css({
	display: 'flex',
	width: '100%',
});

const inputContainerStyles = css({
	position: 'relative',
	width: '100%',
});

const labelOverlayStyles = css({
	alignSelf: 'center',
	display: 'flex',
	position: 'absolute',
	left: token('space.200'),
	top: '0',
	bottom: '0',
	margin: 'auto',
	pointerEvents: 'none',
	gap: token('space.075'),
	cursor: 'text',
});

const buttonContainerStyles = css({
	alignSelf: 'center',
});

type SearchTriggerUIProps = {
	value?: string;
	onChange?: (value: string) => void;
	children?: React.ReactNode;
	className?: string;
	placeholder: React.ReactNode;
	submitButton: React.ReactNode;
	interactive?: boolean;
};

export const SearchTrigger = ({
	children,
	value = '',
	onChange,
	placeholder,
	submitButton,
	interactive = true,
}: SearchTriggerUIProps): JSX.Element => {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const isAtRest = value.length === 0 && !isFocused;
	const isExpanded = !isAtRest || isHovered;
	const VisuallyHiddenPlaceholder = isFocused || value.length > 0 ? VisuallyHidden : Fragment;

	const contextValue = useMemo(() => {
		return {
			isExpanded,
			isHovered,
			isFocused,
			setIsHovered,
			setIsFocused,
			value,
			onChange,
			isAtRest,
			interactive,
		};
	}, [isExpanded, isHovered, isFocused, value, onChange, isAtRest, interactive]);

	const mouseEnterLeaverProps = interactive
		? {
				onMouseEnter: () => setIsHovered(true),
				onMouseLeave: () => setIsHovered(false),
			}
		: {};

	return (
		<SearchTriggerInputContext.Provider value={contextValue}>
			<div
				css={[
					rootStyles,
					interactive && hoverFocusStyles,
					interactive && isExpanded && expandedStyles,
				]}
			>
				<div css={boxStyles} {...mouseEnterLeaverProps}>
					<div css={[layoutStyles]}>
						<div css={inputContainerStyles}>
							<div css={[labelOverlayStyles, isAtRest && subtlePlaceholderStyles]}>
								<AiChatIcon spacing="spacious" label="" />
								<VisuallyHiddenPlaceholder>{placeholder}</VisuallyHiddenPlaceholder>
							</div>
							{children}
						</div>
						<div css={buttonContainerStyles}>{submitButton}</div>
					</div>
				</div>
			</div>
		</SearchTriggerInputContext.Provider>
	);
};
